import {useEffect, useId, useRef, useState, type FormEvent} from 'react';
import {ErrorNotice, Loading, dateTime} from './components';
import type {hermesActions} from './lib/store';
import type {AccountDataResetStatus, HermesState} from './lib/types';
import './account-data-reset-settings.css';

type Props = {state: HermesState; actions: typeof hermesActions};
const phaseLabels: Record<NonNullable<AccountDataResetStatus['phase']>, string> = {
  queued: 'Queued', quiescing: 'Stopping your team and applications',
  reconciling: 'Recording outstanding usage', 'clearing-memory': 'Clearing learned memory',
  'clearing-runtime': 'Clearing files, conversations and VM data', finalizing: 'Finishing account reset',
  complete: 'User data cleared',
};

function ClearDataDialog({state, actions, onClose}: Props & {onClose: () => void}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const titleId = useId(), descriptionId = useId();
  const [confirmation, setConfirmation] = useState('');
  const [attempted, setAttempted] = useState(false);
  const busy = state.operation.label !== null;
  const running = state.accountDataReset.data?.state === 'running';
  useEffect(() => {
    dialog.current?.showModal();
    heading.current?.focus({preventScroll: true});
    if (dialog.current) dialog.current.scrollTop = 0;
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (confirmation !== 'confirm' || busy || running) return;
    setAttempted(true);
    try {
      await actions.clearAccountData(confirmation);
      dialog.current?.close();
    } catch { /* The shared operation record owns the submitted action's error. */ }
  }

  return <dialog ref={dialog} className="account-confirmation-dialog account-data-reset-dialog" aria-labelledby={titleId}
    aria-describedby={descriptionId} onClose={onClose} onCancel={event => {if (busy) event.preventDefault();}}>
    <h2 ref={heading} id={titleId} tabIndex={-1}>Clear all user data</h2>
    <p id={descriptionId}>This permanently deletes your workspace data. It cannot be undone.</p>
    <ul className="account-data-reset-scope">
      <li>Conversations, tasks and scheduled work</li>
      <li>Personal files and learned memory</li>
      <li>Published applications and VM software changes</li>
      <li>Connected Google services and WhatsApp pairing</li>
    </ul>
    <p>Your sign-in, including Google sign-in, profile, subscription, credits and billing history are retained.</p>
    <form aria-busy={busy || running} onSubmit={event => void submit(event)}>
      <ErrorNotice error={attempted && state.operation.scope === 'account' ? state.operation.error : null}/>
      <label className="field"><span>Type <strong>confirm</strong> to continue</span>
        <input value={confirmation} onChange={event => setConfirmation(event.target.value)} disabled={busy || running}
          autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} required pattern="confirm"/></label>
      <div className="button-row">
        <button type="button" className="text-button" disabled={busy} onClick={() => dialog.current?.close()}>Cancel</button>
        <button type="submit" className="button" disabled={busy || running || confirmation !== 'confirm'}>Clear all user data</button>
      </div>
    </form>
  </dialog>;
}

export function AccountDataResetSettings({state, actions}: Props) {
  const titleId = useId();
  const [dialogOpen, setDialogOpen] = useState(false);
  const accountId = state.session.data?.accountId;
  const resource = state.accountDataReset;
  const status = resource.data;
  const running = status?.state === 'running';

  useEffect(() => {
    if (!accountId) return;
    let pending: AbortController | null = null;
    const read = () => {
      if (document.visibilityState !== 'visible' || pending !== null) return;
      const controller = new AbortController();
      pending = controller;
      void actions.refreshAccountDataReset(controller.signal).catch(() => undefined)
        .finally(() => {if (pending === controller) pending = null;});
    };
    read();
    const interval = window.setInterval(read, running ? 3000 : 10000);
    document.addEventListener('visibilitychange', read);
    window.addEventListener('focus', read);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', read);
      window.removeEventListener('focus', read);
      pending?.abort();
    };
  }, [accountId, actions, running]);

  return <section className="settings-section settings-data-reset" aria-labelledby={titleId}>
    <div className="settings-description"><h2 id={titleId}>Clear all user data</h2>
      <p>Start with an empty workspace while keeping your account and subscription.</p>
    </div>
    <div className="account-data-reset-controls">
      <Loading active={resource.status === 'loading' || running}/>
      <ErrorNotice error={resource.error}/>
      <ErrorNotice error={status?.error ? {name: 'AccountDataResetError', message: status.error} : null}/>
      {status && status.state !== 'idle' && <div className="account-data-reset-status" role="status" aria-live="polite">
        <p>{status.state === 'failed' ? 'Clearing stopped before completion.'
          : status.phase === null ? 'Clearing user data' : phaseLabels[status.phase]}</p>
        {status.updatedAt && <p className="field-help">Updated <time dateTime={status.updatedAt}>{dateTime(status.updatedAt)}</time></p>}
      </div>}
      {status?.state === 'failed' && <p className="field-help">Retry to finish clearing your data. Your workspace remains unavailable until it completes.</p>}
      <button type="button" className="button" disabled={state.operation.label !== null || !status || running || state.accountVirtualMachine.data?.recovery.state === 'running'}
        onClick={() => setDialogOpen(true)}>Clear all user data</button>
      {dialogOpen && <ClearDataDialog state={state} actions={actions} onClose={() => setDialogOpen(false)}/>}
    </div>
  </section>;
}
